import { useTranslation } from 'react-i18next';
import { ROUTES } from 'routes/routes';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { ContentHeader } from 'components/ui/layout/ContentHeader';

import { useFloatNotification } from 'contexts/FloatNotificationContext';
import {
  useGetAllOrganizations,
  useDeleteOrganization,
} from 'api/services/organizationService';
import { useUI } from 'contexts/UiContext';
import { ColumnAction, DarwinListView } from 'components/darwin-ui';
import { getColumns, DELETE_ACTION_KEY } from './columns';
import { useCallback, useEffect, useState } from 'react';
import { Company } from 'types/organizationTypes';
import useTableControls from 'hooks/useTableControls';
import { ApproveDataSharingModal } from 'components/ui/organizations/ApproveDataSharingModal/ApproveDataSharingModal';
import { GenericDeleteModal } from 'components/ui/GenericDeleteModal';

export const Organizations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openNotification } = useFloatNotification();
  const { selectedCompany } = useUI();

  const { companyId, targetCompanyId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const [showDataSharingApproveModal, setShowDataSharingApproveModal] =
    useState(!!companyId);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] =
    useState<Company | null>(null);

  const handleCloseApproveModal = useCallback(() => {
    setShowDataSharingApproveModal(false);
    navigate(ROUTES.ORGANIZATIONS.LIST);
  }, [setShowDataSharingApproveModal, navigate]);

  const {
    currentPage,
    pageSize,
    searchQuery,
    setSearchQuery,
    setCurrentPage,
    onPageSizeChange,
    sortOrder,
    onTableChange,
  } = useTableControls({ defaultSortBy: 'name' });

  const {
    data: organizationsData,
    isLoading: isLoadingOrganizations,
    isFetching: isFetchingOrganizations,
    isPending: isPendingOrganizations,
    error: organizationsError,
  } = useGetAllOrganizations({
    page: currentPage,
    limit: pageSize,
    query: searchQuery,
    sortOrder,
  });

  const deleteOrganizationMutation = useDeleteOrganization();

  useEffect(() => {
    if (organizationsError?.message) {
      openNotification(
        'Error',
        t('organization.organizations-list.loading-error'),
        'error'
      );
    }
  }, [openNotification, organizationsError?.message, t]);

  const isLoading =
    isLoadingOrganizations || isFetchingOrganizations || isPendingOrganizations;

  const tableData = organizationsData?.data ?? [];

  const onClick = () => {
    navigate(ROUTES.ORGANIZATIONS.CREATE);
  };

  const handleDeleteOrganization = useCallback(async (company: Company) => {
    setOrganizationToDelete(company);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!organizationToDelete) return;

    try {
      await deleteOrganizationMutation.mutateAsync({
        companyId: selectedCompany!,
        targetCompanyId: organizationToDelete.id,
      });
      openNotification(
        'Success',
        t('organization.organizations-list.delete-success'),
        'success'
      );
      setShowDeleteModal(false);
      setOrganizationToDelete(null);
    } catch (error) {
      openNotification(
        'Error',
        t('organization.organizations-list.delete-error'),
        'error'
      );
    }
  }, [
    deleteOrganizationMutation,
    selectedCompany,
    openNotification,
    t,
    organizationToDelete,
  ]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setOrganizationToDelete(null);
  }, []);

  const actions: ColumnAction<Company>[] = [
    {
      key: 'edit',
      name: 'edit',
      label: t('list-view.action-items.edit'),
      onClick: company => {
        navigate(generatePath(ROUTES.ORGANIZATIONS.EDIT, { id: company.id }));
      },
    },
    {
      key: DELETE_ACTION_KEY,
      name: 'delete',
      label: t('list-view.action-items.delete'),
      onClick: handleDeleteOrganization,
    },
  ];

  const columns = getColumns(t, actions, searchQuery);

  return (
    <>
      <ContentHeader
        title={t('organization.organizations-list.title')}
        onSave={onClick}
        onSaveTitle={t('organization.organizations-list.new-organization')}
      />

      <DarwinListView<Company>
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        pagination={{
          total: organizationsData?.metadata?.total,
          pageSize,
          currentPage,
          onPageChange: setCurrentPage,
          onPageSizeChange,
        }}
        header={{
          setSearchQuery,
          searchPlaceholder: t(
            'organization.organizations-list.search-placeholder'
          ),
        }}
        table={{
          onChange: onTableChange,
        }}
        behavior={{
          onRowClick: company => {
            navigate(
              generatePath(ROUTES.ORGANIZATIONS.EDIT, { id: company.id })
            );
          },
        }}
      />

      <ApproveDataSharingModal
        open={!!showDataSharingApproveModal}
        onCancel={handleCloseApproveModal}
        token={token || ''}
        targetCompanyId={targetCompanyId || ''}
        companyId={companyId || ''}
      />

      <GenericDeleteModal
        open={showDeleteModal}
        onCancel={handleCancelDelete}
        onOk={handleConfirmDelete}
        confirmLoading={deleteOrganizationMutation.isPending}
        bodyTitle={t('organization.organizations-list.confirm-delete-title')}
        bodyText={t('organization.organizations-list.confirm-delete-text', {
          name: organizationToDelete?.name,
        })}
        okTitle={t('delete')}
      />
    </>
  );
};

export default Organizations;
